import { NativeStackHeaderProps, Stack, useRouter } from 'expo-router';
import { ENTRY_POINT_TIMEOUT } from 'features/advance/constants/constants';
import useWhereToGo, {
  AdvanceDestination,
} from 'features/advance/hooks/useWhereToGo';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import LoadingSpinner from 'shared/components/vxl/loading/LoadingSpinner/LoadingSpinner';
import ExpoNavigationBar from 'shared/components/vxl/navigation/ExpoNavigationBar/ExpoNavigationBar';
import ErrorScreen, {
  ErrorScreenType,
} from 'shared/patterns/vxl/errors/ErrorScreen/ErrorScreen';
import ScreenTransitionOverlay from 'shared/patterns/vxl/ScreenTransitionOverlay/ScreenTransitionOverlay';
import theme from 'shared/styles/theme';

const AdvanceEntryPoint = () => {
  const router = useRouter();
  const { status, advanceAccount, fetching, error } = useWhereToGo();
  const { t: translatedText } = useTranslation('advance', {
    keyPrefix: 'error',
  });

  useEffect(() => {
    let id: ReturnType<typeof setTimeout>;

    if (status) {
      switch (status) {
        case AdvanceDestination.Registration:
          router.replace('/varo-advance/employment-info');
          break;
        case AdvanceDestination.TakeOutAdvance:
        case AdvanceDestination.Qualified:
          router.replace({
            pathname: '/varo-advance/new',
            params: { id: advanceAccount?.id },
          });
          break;
        case AdvanceDestination.Overdue:
        case AdvanceDestination.Active:
          if (advanceAccount?.id) {
            router.replace({
              pathname: `/accounts/[id]`,
              params: { id: advanceAccount?.id },
            });
          }
          break;
        case AdvanceDestination.PartiallyQualified:
          router.replace({
            pathname: '/accounts/varo-advance/partially-qualified',
            params: { id: advanceAccount?.id },
          });
          break;
        case AdvanceDestination.NotQualified:
          router.replace('/varo-advance/link-account/intro');
          break;
        case AdvanceDestination.Processing:
          router.replace('/varo-advance/processing');
          break;
        case AdvanceDestination.Banned:
          router.replace('/');
          break;
        case AdvanceDestination.Unknown:
        default:
          router.replace({
            pathname: '/varo-advance/error',
            params: {
              subcategory: 'va_application',
              error_type: 'unknown',
              error_description: error?.messageBody,
              step_name: 'va_application_submission',
            },
          });
          break;
      }
    } else {
      id = setTimeout(() => {
        router.replace({
          pathname: '/varo-advance/error',
          params: {
            subcategory: 'va_application',
            error_type: 'timeout',
            error_description: error?.messageBody,
            step_name: 'va_application_submission',
          },
        });
      }, ENTRY_POINT_TIMEOUT);
    }

    return () => clearTimeout(id);
  }, [advanceAccount?.id, error?.messageBody, status, router]);

  return (
    <View style={styles.container}>
      <Stack.Screen
        options={{
          headerShown: true,
          headerBackVisible: true,
          header: (props: NativeStackHeaderProps) => (
            <ExpoNavigationBar
              {...props}
              options={{
                headerShown: false,
                headerBackVisible: false,
              }}
            />
          ),
        }}
      />

      <ScreenTransitionOverlay
        isLoading={fetching}
        LoadingComponent={<LoadingSpinner />}
        ErrorComponent={
          error ? (
            <ErrorScreen
              screenType={ErrorScreenType.Error}
              messageHeadline={translatedText('title')}
              messageBody={translatedText('body')}
              primaryButton={{
                title: translatedText('button'),
                onPress: () => router.navigate('/'),
              }}
            />
          ) : null
        }
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
  },
});

export default AdvanceEntryPoint;
